import { Id } from '@nextforce/api'
import { useQuery } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import { SingleSelectSetting } from 'settings/types'
import { convertStoreSettings, convertUserSettings } from './shared/convert'
import keys from './shared/keys'
import * as Response from './shared/response'
import {
  SettingsSource,
  StoreSettingsData,
  UserSettingsData,
} from './shared/types'

export const LOCALE_SETTING_ID = 'language'
export const THEME_SETTING_ID = 'theme'

export function useSettings(
  type: SettingsSource['type'],
  storeId: Id | undefined
) {
  const userSettingsQuery = useUserSettings(type, {
    enabled: type === 'user' ? true : false,
  })

  const storeSettingsQuery = useStoreSettings(type, storeId)

  if (type === 'user') {
    return userSettingsQuery
  } else {
    return storeSettingsQuery
  }
}

export function useUserSettings<T = UserSettingsData>(
  type: SettingsSource['type'],
  {
    select,
    refetchOnMount,
    enabled = true,
  }: {
    enabled?: boolean
    select?: (data: UserSettingsData) => T
    refetchOnMount?: boolean | 'always'
  } = {}
) {
  const { server } = useServer()

  return useQuery<UserSettingsData, unknown, T>({
    queryKey: keys.user(),
    queryFn: fetchUserSettings,
    select,
    refetchOnMount,
    enabled: enabled && type === 'user',
  })

  async function fetchUserSettings() {
    const url = '/v2/users/me/preferences'
    const response = await server.get<Response.Settings>(url)
    const { settings } = convertUserSettings(response, url)
    return {
      settings: settings.filter(setting => setting.id !== LOCALE_SETTING_ID),
    }
  }
}

export function useStoreSettings(
  type: SettingsSource['type'],
  storeId: Id | undefined
) {
  const { server } = useServer()
  return useQuery<StoreSettingsData>({
    queryKey: storeId ? keys.store(storeId) : [],
    queryFn: fetchStoreSettings,
    enabled: !!storeId && type === 'store',
  })

  async function fetchStoreSettings() {
    if (!storeId) {
      throw new Error('Illegal store ID')
    }

    const url = `/v2/helpers/me/stores/${storeId}/profile`
    const response = await server.get<Response.StoreSettings>(url)
    return convertStoreSettings(response, url)
  }
}

export function useUserLocaleSetting(enabled: boolean) {
  return useUserSettings('user', {
    enabled,
    select: settingsData => {
      return settingsData.settings.find(
        setting => setting.id === LOCALE_SETTING_ID
      ) as SingleSelectSetting | null
    },
  })
}

export function useUserThemeSetting() {
  return useUserSettings('user', {
    select: settingsData => {
      return settingsData.settings.find(
        setting => setting.id === 'theme'
      ) as SingleSelectSetting | null
    },
    refetchOnMount: 'always',
  })
}
